// ============================================================
//  src/pages/LandingPage.js
//  CommandKit — marketing landing page (redesign)
//  All styles are scoped under .ck in LandingPage.css
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

// ── Icons (inline, stroke-based) ─────────────────────────────
const I = {
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  flow: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><path d="M6.5 10v3a2 2 0 0 0 2 2H14" /></>,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
  megaphone: <><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  db: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" /><path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" /></>,
  flask: <><path d="M9 3h6M10 3v6L4.5 18.5A1.7 1.7 0 0 0 6 21h12a1.7 1.7 0 0 0 1.5-2.5L14 9V3" /><path d="M7 15h10" /></>,
  code: <path d="m16 18 6-6-6-6M8 6l-6 6 6 6" />,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  bolt: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
};

function Icon({ name, size = 20, className = '' }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {I[name]}
    </svg>
  );
}

function Logo() {
  return (
    <span className="ck-logo">
      <span className="ck-logo-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m7 8-4 4 4 4M17 8l4 4-4 4M14 5l-4 14" />
        </svg>
      </span>
      <span className="ck-logo-text">CommandKit</span>
    </span>
  );
}

// ── Scroll reveal ────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.ck [data-reveal]');
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-in'));
      return undefined;
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// ── Animated chat demo ───────────────────────────────────────
const CHAT_SCRIPT = [
  { from: 'user', text: 'Hi, I want to buy data', delay: 500 },
  { from: 'bot',  text: 'Welcome back, Tolu 👋 Which plan would you like?', delay: 900,
    buttons: ['1GB · ₦300', '2GB · ₦500', '5GB · ₦1,200'] },
  { from: 'user', text: '2GB · ₦500', delay: 700 },
  { from: 'bot',  text: 'Done ✅ 2GB has been sent to 0803 *** 4412.\nRef: CK-88213', delay: 1200 },
];

function ChatDemo() {
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    let i = 0;
    let alive = true;
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(() => alive && fn(), ms));

    function next() {
      if (i >= CHAT_SCRIPT.length) {
        later(() => { setShown([]); i = 0; next(); }, 4200);
        return;
      }
      const msg = CHAT_SCRIPT[i];
      setTyping(msg.from === 'bot');
      later(() => {
        setTyping(false);
        setShown(prev => [...prev, msg]);
        i += 1;
        later(next, msg.delay);
      }, msg.from === 'bot' ? 1100 : 350);
    }
    later(next, 700);
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [shown, typing]);

  return (
    <div className="ck-phone">
      <div className="ck-phone-head">
        <span className="ck-phone-avatar">D</span>
        <div>
          <div className="ck-phone-name">DataHub NG</div>
          <div className="ck-phone-status">Business account · online</div>
        </div>
      </div>
      <div className="ck-phone-body" ref={bodyRef}>
        <div className="ck-phone-day">Today</div>
        {shown.map((m, idx) => (
          <div key={idx} className={`ck-msg ck-msg-${m.from}`}>
            <div className="ck-bubble">
              {m.text}
              {m.buttons && (
                <div className="ck-bubble-btns">
                  {m.buttons.map(b => <span key={b}>{b}</span>)}
                </div>
              )}
            </div>
          </div>
        ))}
        {typing && (
          <div className="ck-msg ck-msg-bot">
            <div className="ck-bubble ck-typing"><i /><i /><i /></div>
          </div>
        )}
      </div>
      <div className="ck-phone-input">
        <span>Message</span>
        <span className="ck-phone-send"><Icon name="arrow" size={14} /></span>
      </div>
    </div>
  );
}

// ── Data ─────────────────────────────────────────────────────
const USE_CASES = ['Data & airtime', 'Online stores', 'Customer support', 'Bookings', 'Logistics updates', 'Payments & wallets'];

const STEPS = [
  { n: '01', title: 'Connect your number', desc: 'Link your Meta Business account and WhatsApp number. Our guided setup takes about three minutes — no developer needed.' },
  { n: '02', title: 'Build commands & flows', desc: 'Add keyword triggers, menus and buttons in the visual builder, or drop down to JSON actions when you want full control.' },
  { n: '03', title: 'Go live and grow', desc: 'Switch your bot on. It answers, sells and follows up around the clock, and hands tricky chats to your team.' },
];

const PLANS = [
  {
    name: 'Free', price: '₦0', period: 'forever', blurb: 'For trying things out and small side projects.',
    features: ['2 bots', 'Basic flows', '100 messages / day', 'Community access'], cta: 'Get started free',
  },
  {
    name: 'Pro', price: '₦5,000', period: '30 days', blurb: 'For growing businesses selling on WhatsApp daily.', featured: true,
    features: ['5 bots', 'WhatsApp Flows', 'Scheduled messages', 'Broadcast to all users', 'Priority support'], cta: 'Start with Pro',
  },
  {
    name: 'Business', price: '₦15,000', period: '30 days', blurb: 'For teams running several bots and brands.',
    features: ['Unlimited bots', 'Team collaborators', 'Advanced analytics', 'Custom integrations', 'Dedicated support'], cta: 'Talk to us',
  },
];

const FAQS = [
  {
    q: 'Do I need a WhatsApp Business API account?',
    a: 'You need a Meta Business account, which is free to create. CommandKit walks you through connecting your number to the official WhatsApp Business Platform during onboarding — no technical setup required.',
  },
  {
    q: 'Do I need to know how to code?',
    a: 'No. Most bots are built entirely with the visual flow builder. If you are a developer, you can also write JSON actions, call your own API with HTTP requests and use variables anywhere in your messages.',
  },
  {
    q: 'Will my customers know they are talking to a bot?',
    a: 'That is up to you. Most bots mix instant automated replies with a hand-off to the Agent Inbox, so customers can always reach a real person when they need one.',
  },
  {
    q: 'What happens if I go over the Free plan limit?',
    a: 'Your bot keeps running, but extra messages pause until the next day or until you upgrade. You get an in-app notice before you reach the limit.',
  },
  {
    q: 'Can I cancel or change plans anytime?',
    a: 'Yes. There are no contracts. Upgrade, downgrade or cancel from your dashboard whenever you like — changes apply from your next billing cycle.',
  },
  {
    q: 'Is my customer data safe?',
    a: 'Messages run through Meta’s official WhatsApp Business Platform, and your account data is encrypted at rest. We never sell or share your customers’ data.',
  },
];

// ── Bento visuals ────────────────────────────────────────────
function FlowVisual() {
  return (
    <div className="ck-flow" aria-hidden="true">
      <div className="ck-node ck-node-trigger">
        <span className="ck-node-tag">Trigger</span>
        <b>Keyword: “buy data”</b>
      </div>
      <span className="ck-wire" />
      <div className="ck-node">
        <span className="ck-node-tag">Send list</span>
        <b>Choose a data plan</b>
      </div>
      <span className="ck-wire" />
      <div className="ck-node">
        <span className="ck-node-tag">HTTP request</span>
        <b>POST /api/vend</b>
      </div>
      <div className="ck-branch">
        <div className="ck-branch-col">
          <span className="ck-wire ck-wire-ok" />
          <div className="ck-node ck-node-ok"><span className="ck-node-tag">Success</span><b>Send receipt</b></div>
        </div>
        <div className="ck-branch-col">
          <span className="ck-wire ck-wire-warn" />
          <div className="ck-node ck-node-warn"><span className="ck-node-tag">Failed</span><b>Hand off to agent</b></div>
        </div>
      </div>
    </div>
  );
}

function InboxVisual() {
  const rows = [
    { i: 'AM', n: 'Amaka', t: 'Can I change my delivery address?', u: 2 },
    { i: 'SK', n: 'Sani K.', t: 'Payment went through, thanks!', u: 0 },
    { i: 'BO', n: 'Bisi O.', t: 'Need help with my order', u: 1 },
  ];
  return (
    <div className="ck-inbox" aria-hidden="true">
      {rows.map(r => (
        <div key={r.n} className="ck-inbox-row">
          <span className="ck-inbox-av">{r.i}</span>
          <span className="ck-inbox-txt"><b>{r.n}</b><span>{r.t}</span></span>
          {r.u > 0 && <span className="ck-inbox-badge">{r.u}</span>}
        </div>
      ))}
    </div>
  );
}

function BarsVisual() {
  const bars = [38, 52, 45, 64, 58, 80, 72];
  return (
    <div className="ck-bars" aria-hidden="true">
      {bars.map((h, i) => <span key={i} style={{ height: `${h}%` }} className={i === 5 ? 'is-peak' : ''} />)}
    </div>
  );
}

function BroadcastVisual() {
  return (
    <div className="ck-bcast" aria-hidden="true">
      <div className="ck-bcast-top"><b>Weekend promo</b><span>Sending</span></div>
      <div className="ck-bcast-track"><span style={{ width: '78%' }} /></div>
      <div className="ck-bcast-meta"><span>Delivered</span><span>78%</span></div>
    </div>
  );
}

function ScheduleVisual() {
  const items = [['08:00', 'Morning reminder'], ['12:30', 'Order follow-up'], ['18:00', 'Daily summary']];
  return (
    <div className="ck-sched" aria-hidden="true">
      {items.map(([t, l]) => (
        <div key={t} className="ck-sched-row"><span>{t}</span><b>{l}</b></div>
      ))}
    </div>
  );
}

// ── FAQ item ─────────────────────────────────────────────────
function FaqItem({ q, a, open, onToggle, id }) {
  return (
    <div className={`ck-faq-item ${open ? 'is-open' : ''}`}>
      <button className="ck-faq-q" onClick={onToggle} aria-expanded={open} aria-controls={`faq-${id}`}>
        <span>{q}</span>
        <span className="ck-faq-icon"><Icon name="plus" size={18} /></span>
      </button>
      <div className="ck-faq-a" id={`faq-${id}`} role="region">
        <div><p>{a}</p></div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const go = path => { setMenuOpen(false); navigate(path); };
  const close = () => setMenuOpen(false);

  return (
    <div className="ck">

      {/* ── Nav ── */}
      <header className={`ck-nav ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'is-open' : ''}`}>
        <div className="ck-wrap ck-nav-inner">
          <a href="#top" className="ck-nav-brand" onClick={close}><Logo /></a>
          <nav className="ck-nav-links" aria-label="Main">
            <a href="#features" onClick={close}>Features</a>
            <a href="#how" onClick={close}>How it works</a>
            <a href="#developers" onClick={close}>Developers</a>
            <a href="#pricing" onClick={close}>Pricing</a>
            <a href="#faq" onClick={close}>FAQ</a>
          </nav>
          <div className="ck-nav-actions">
            <button className="ck-btn ck-btn-text" onClick={() => go('/login')}>Sign in</button>
            <button className="ck-btn ck-btn-dark" onClick={() => go('/register')}>Get started</button>
          </div>
          <button className="ck-nav-toggle" onClick={() => setMenuOpen(o => !o)} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen}>
            <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
          </button>
        </div>
      </header>

      <main id="top">
        {/* ── Hero ── */}
        <section className="ck-hero">
          <div className="ck-hero-bg" aria-hidden="true" />
          <div className="ck-wrap ck-hero-grid">
            <div className="ck-hero-copy">
              <span className="ck-pill">
                <span className="ck-pill-dot" />
                Built on the official WhatsApp Business Platform
              </span>
              <h1 className="ck-h1">
                Turn WhatsApp chats into <span className="ck-h1-accent">finished sales.</span>
              </h1>
              <p className="ck-lead">
                CommandKit lets you build WhatsApp bots that answer customers, take orders and
                confirm payments on their own — designed visually, powerful enough for developers.
              </p>
              <div className="ck-hero-ctas">
                <button className="ck-btn ck-btn-primary ck-btn-lg" onClick={() => navigate('/register')}>
                  Start building free <Icon name="arrow" size={18} />
                </button>
                <a className="ck-btn ck-btn-ghost ck-btn-lg" href="#how">See how it works</a>
              </div>
              <ul className="ck-hero-trust">
                <li><Icon name="check" size={16} /> No card required</li>
                <li><Icon name="check" size={16} /> 2 bots free forever</li>
                <li><Icon name="check" size={16} /> Live in minutes</li>
              </ul>
            </div>

            <div className="ck-hero-visual">
              <ChatDemo />
              <div className="ck-float ck-float-a">
                <span className="ck-float-ic"><Icon name="check" size={16} /></span>
                <div><b>Payment confirmed</b><span>₦500 · just now</span></div>
              </div>
              <div className="ck-float ck-float-b">
                <span className="ck-float-ic ck-float-ic-dark"><Icon name="bolt" size={15} /></span>
                <div><b>Replied in 0.8s</b><span>Automated · 24/7</span></div>
              </div>
            </div>
          </div>

          <div className="ck-wrap ck-usecases">
            <span className="ck-usecases-label">Teams use CommandKit for</span>
            <div className="ck-usecases-list">
              {USE_CASES.map(u => <span key={u}>{u}</span>)}
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="ck-section" id="features">
          <div className="ck-wrap">
            <div className="ck-head ck-head-split" data-reveal>
              <div>
                <span className="ck-eyebrow">Features</span>
                <h2 className="ck-h2">Everything you need to run your business on WhatsApp.</h2>
              </div>
              <p className="ck-sub">From a simple auto-reply to a full sales funnel with payments and human hand-off — all from one dashboard.</p>
            </div>

            <div className="ck-bento">
              <article className="ck-card ck-card-flow" data-reveal>
                <div className="ck-card-copy">
                  <span className="ck-card-ic"><Icon name="flow" /></span>
                  <h3>Visual flow builder</h3>
                  <p>Drag, drop and connect steps to build purchases, bookings and support journeys. Branch on success or failure without writing code.</p>
                </div>
                <FlowVisual />
              </article>

              <article className="ck-card ck-card-inbox" data-reveal>
                <div className="ck-card-copy">
                  <span className="ck-card-ic"><Icon name="inbox" /></span>
                  <h3>Agent inbox</h3>
                  <p>When a chat needs a human, your team picks it up in a shared inbox.</p>
                </div>
                <InboxVisual />
              </article>

              <article className="ck-card" data-reveal>
                <div className="ck-card-copy">
                  <span className="ck-card-ic"><Icon name="megaphone" /></span>
                  <h3>Broadcasts</h3>
                  <p>Reach every bot user at once with promos and updates.</p>
                </div>
                <BroadcastVisual />
              </article>

              <article className="ck-card" data-reveal>
                <div className="ck-card-copy">
                  <span className="ck-card-ic"><Icon name="clock" /></span>
                  <h3>Scheduled messages</h3>
                  <p>Reminders and follow-ups that go out at exactly the right time.</p>
                </div>
                <ScheduleVisual />
              </article>

              <article className="ck-card" data-reveal>
                <div className="ck-card-copy">
                  <span className="ck-card-ic"><Icon name="chart" /></span>
                  <h3>Real-time analytics</h3>
                  <p>See who’s chatting, what they tap and where flows drop off.</p>
                </div>
                <BarsVisual />
              </article>
            </div>

            <div className="ck-minis" data-reveal>
              <div className="ck-mini">
                <span className="ck-mini-ic"><Icon name="users" size={18} /></span>
                <div><h4>Team collaboration</h4><p>Invite teammates with roles and permissions.</p></div>
              </div>
              <div className="ck-mini">
                <span className="ck-mini-ic"><Icon name="db" size={18} /></span>
                <div><h4>Built-in storage</h4><p>Save user data and reuse it anywhere in replies.</p></div>
              </div>
              <div className="ck-mini">
                <span className="ck-mini-ic"><Icon name="flask" size={18} /></span>
                <div><h4>Test sandbox</h4><p>Step through every action before you go live.</p></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="ck-section ck-section-soft" id="how">
          <div className="ck-wrap">
            <div className="ck-head ck-head-center" data-reveal>
              <span className="ck-eyebrow">How it works</span>
              <h2 className="ck-h2">From sign-up to your first live bot in three steps.</h2>
            </div>
            <ol className="ck-steps">
              {STEPS.map(s => (
                <li key={s.n} className="ck-step" data-reveal>
                  <span className="ck-step-n">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── Developers ── */}
        <section className="ck-section ck-section-dark" id="developers">
          <div className="ck-wrap ck-dev-grid">
            <div className="ck-dev-copy" data-reveal>
              <span className="ck-eyebrow ck-eyebrow-dark">For developers</span>
              <h2 className="ck-h2">No-code by default. Real code when you need it.</h2>
              <p className="ck-sub">
                Every command compiles to plain JSON actions. Call your own API, store values per
                user and drop them into any message with placeholders.
              </p>
              <ul className="ck-dev-list">
                <li><Icon name="check" size={16} /> Ready-made actions for lists, buttons, CTA links, payments, loops and HTTP calls</li>
                <li><Icon name="check" size={16} /> <span><code>{'{{placeholders}}'}</code> in any message or request</span></li>
                <li><Icon name="check" size={16} /> Webhooks, endpoint logs and an error view that shows what failed</li>
                <li><Icon name="check" size={16} /> Import and export bots as JSON</li>
              </ul>
            </div>

            <div className="ck-code" data-reveal>
              <div className="ck-code-bar">
                <span /><span /><span />
                <em>commands/buy-data.json</em>
              </div>
              <pre><code>
<span className="t-p">[</span>{'\n'}
{'  '}<span className="t-p">{'{'}</span>{'\n'}
{'    '}<span className="t-k">"type"</span>: <span className="t-s">"http_post"</span>,{'\n'}
{'    '}<span className="t-k">"url"</span>: <span className="t-s">"https://api.yourstore.ng/vend"</span>,{'\n'}
{'    '}<span className="t-k">"body"</span>: <span className="t-p">{'{'}</span>{'\n'}
{'      '}<span className="t-k">"phone"</span>: <span className="t-s">"<span className="t-v">{'{{chat_id}}'}</span>"</span>,{'\n'}
{'      '}<span className="t-k">"plan"</span>: <span className="t-s">"<span className="t-v">{'{{storage.plan}}'}</span>"</span>{'\n'}
{'    '}<span className="t-p">{'}'}</span>,{'\n'}
{'    '}<span className="t-k">"save_as"</span>: <span className="t-s">"order"</span>{'\n'}
{'  '}<span className="t-p">{'}'}</span>,{'\n'}
{'  '}<span className="t-p">{'{'}</span>{'\n'}
{'    '}<span className="t-k">"type"</span>: <span className="t-s">"send_text"</span>,{'\n'}
{'    '}<span className="t-k">"text"</span>: <span className="t-s">"Done ✅ Ref: <span className="t-v">{'{{order.ref}}'}</span>"</span>{'\n'}
{'  '}<span className="t-p">{'}'}</span>{'\n'}
<span className="t-p">]</span>
              </code></pre>
            </div>
          </div>
        </section>

        {/* ── Pricing ── */}
        <section className="ck-section" id="pricing">
          <div className="ck-wrap">
            <div className="ck-head ck-head-center" data-reveal>
              <span className="ck-eyebrow">Pricing</span>
              <h2 className="ck-h2">Start free. Upgrade when you’re ready.</h2>
              <p className="ck-sub">Simple naira pricing. No hidden fees, cancel anytime.</p>
            </div>
            <div className="ck-plans">
              {PLANS.map(p => (
                <div key={p.name} className={`ck-plan ${p.featured ? 'is-featured' : ''}`} data-reveal>
                  <div className="ck-plan-top">
                    <h3>{p.name}</h3>
                    {p.featured && <span className="ck-plan-badge">Most popular</span>}
                  </div>
                  <p className="ck-plan-blurb">{p.blurb}</p>
                  <div className="ck-plan-price">{p.price}<span>/ {p.period}</span></div>
                  <button
                    className={`ck-btn ck-btn-block ${p.featured ? 'ck-btn-primary' : 'ck-btn-outline'}`}
                    onClick={() => navigate('/register')}
                  >
                    {p.cta}
                  </button>
                  <ul className="ck-plan-list">
                    {p.features.map(f => <li key={f}><Icon name="check" size={16} /> {f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="ck-section ck-section-soft" id="faq">
          <div className="ck-wrap ck-faq-grid">
            <div className="ck-faq-side" data-reveal>
              <span className="ck-eyebrow">FAQ</span>
              <h2 className="ck-h2">Questions, answered.</h2>
              <p className="ck-sub">Can’t find what you’re looking for? Create a free account and reach us from the support page in your dashboard.</p>
            </div>
            <div className="ck-faq-list" data-reveal>
              {FAQS.map((f, i) => (
                <FaqItem key={f.q} id={i} q={f.q} a={f.a} open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="ck-cta-wrap">
          <div className="ck-wrap">
            <div className="ck-cta" data-reveal>
              <div className="ck-cta-glow" aria-hidden="true" />
              <h2>Your first bot is a few minutes away.</h2>
              <p>Create a free account, connect your number and start replying to customers automatically today.</p>
              <div className="ck-cta-actions">
                <button className="ck-btn ck-btn-primary ck-btn-lg" onClick={() => navigate('/register')}>
                  Create free account <Icon name="arrow" size={18} />
                </button>
                <button className="ck-btn ck-btn-ondark ck-btn-lg" onClick={() => navigate('/login')}>Sign in</button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="ck-footer">
        <div className="ck-wrap ck-footer-grid">
          <div className="ck-footer-brand">
            <Logo />
            <p>WhatsApp automation for businesses that sell in chat. Made in Nigeria.</p>
          </div>
          <div className="ck-footer-col">
            <h5>Product</h5>
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#developers">Developers</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="ck-footer-col">
            <h5>Account</h5>
            <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }}>Sign in</a>
            <a href="/register" onClick={e => { e.preventDefault(); navigate('/register'); }}>Create account</a>
            <a href="/forgot-password" onClick={e => { e.preventDefault(); navigate('/forgot-password'); }}>Reset password</a>
          </div>
          <div className="ck-footer-col">
            <h5>Help</h5>
            <a href="#faq">FAQ</a>
            <a href="/agent" onClick={e => { e.preventDefault(); navigate('/agent'); }}>Agent login</a>
          </div>
        </div>
        <div className="ck-wrap ck-footer-bottom">
          <span>© {new Date().getFullYear()} CommandKit. All rights reserved.</span>
          <span className="ck-footer-meta"><Icon name="shield" size={14} /> Official WhatsApp Business Platform</span>
        </div>
      </footer>

      {/* ── Mobile menu sheet ── */}
      <div className={`ck-sheet ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <a href="#features" onClick={close}>Features</a>
        <a href="#how" onClick={close}>How it works</a>
        <a href="#developers" onClick={close}>Developers</a>
        <a href="#pricing" onClick={close}>Pricing</a>
        <a href="#faq" onClick={close}>FAQ</a>
        <div className="ck-sheet-actions">
          <button className="ck-btn ck-btn-outline ck-btn-block" onClick={() => go('/login')}>Sign in</button>
          <button className="ck-btn ck-btn-primary ck-btn-block" onClick={() => go('/register')}>Get started free</button>
        </div>
      </div>

      {/* ── Mobile sticky CTA ── */}
      <div className={`ck-sticky ${scrolled && !menuOpen ? 'is-visible' : ''}`}>
        <button className="ck-btn ck-btn-primary ck-btn-block" onClick={() => navigate('/register')}>
          Start building free <Icon name="arrow" size={18} />
        </button>
      </div>
    </div>
  );
}